import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { api, getAnonId } from "@/lib/api";
import { isNative } from "@/lib/session";
import { useUser } from "@/store/user";
import {
  NATIVE_CALLBACK_PREFIX,
  isGooglePending,
  clearGooglePending,
  closeBrowser,
  finishLogin,
} from "@/lib/nativeAuth";

// Receives balagha://auth/callback deep links and completes Google sign-in.
// Shows a full-screen loader (no detour through /login) and processes each
// session_id once, even if Android delivers the link twice.
export const NativeAuthListener = () => {
  const navigate = useNavigate();
  const { refresh } = useUser();
  const [busy, setBusy] = useState(false);
  const seen = useRef(new Set());
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isNative) return undefined;
    const subs = [];
    const onUrl = async ({ url }) => {
      if (!url?.startsWith(NATIVE_CALLBACK_PREFIX)) return;
      clearGooglePending();
      closeBrowser();
      const sid = new URL(url).searchParams.get("session_id");
      if (!sid) {
        if (!inFlight.current) navigate("/login?err=google", { replace: true });
        return;
      }
      if (inFlight.current || seen.current.has(sid)) return;
      seen.current.add(sid);
      inFlight.current = true;
      setBusy(true);
      try {
        const res = await api.exchangeSession(sid, getAnonId());
        const me = await finishLogin(res);
        await refresh(me);
        navigate("/", { replace: true });
      } catch (e) {
        navigate(`/login?err=${e?.code === "session" ? "session" : "google"}`, { replace: true });
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    };
    const onBrowserClosed = () => {
      // Give a late-arriving deep link a moment before treating it as a cancel.
      setTimeout(() => {
        if (!isGooglePending() || inFlight.current) return;
        clearGooglePending();
        navigate("/login?err=google_cancel", { replace: true });
      }, 1200);
    };
    CapApp.addListener("appUrlOpen", onUrl).then((s) => subs.push(s));
    Browser.addListener("browserFinished", onBrowserClosed).then((s) => subs.push(s));
    return () => subs.forEach((s) => s.remove());
  }, [navigate, refresh]);

  if (!busy) return null;
  return (
    <div className="native-auth-overlay" role="status" aria-live="polite" data-testid="native-auth-overlay">
      <div className="native-auth-spinner" aria-hidden="true" />
      <p>جارٍ إكمال تسجيل الدخول…</p>
    </div>
  );
};
